/**
 * Backups leaving the device as files. Every backup is a zip — the ledger as
 * `ledger.sqlite` and its attached files, readable, as `attachments/<id>` — a
 * plain one here, a password-protected one in the backup panel (the same
 * entries, `securezip.js`). The ledger goes in without what sync added and
 * without its files' keys: the files beside it are readable. Restoring reads
 * either kind, and the bare `.sqlite` backups of before (`readBackup`).
 */
import { exportDatabase, flushDatabase } from '../db'
import { stripSync } from '../db/strip'
import { today } from '../lib/dates'
import { zip } from '../lib/zip'
import { filesForBackup } from './attachments'

export const backupStem = () => `okane-${today()}`

export function saveFile(bytes, name, type) {
  const url = URL.createObjectURL(new Blob([bytes], { type }))
  const link = document.createElement('a')
  link.href = url
  link.download = name
  link.click()
  URL.revokeObjectURL(url)
}

/** The whole database as it stands — written out first, where a driver buffers. */
export async function databaseBytes() {
  await flushDatabase()
  return exportDatabase()
}

/**
 * A backup's entries: `[{ name, bytes }]`, and the ids of files the ledger
 * names that could not be read (not on this device, and no server to ask).
 */
export async function backupEntries(onProgress) {
  const ledger = await stripSync(await databaseBytes(), 'UPDATE attachments SET file_key = NULL')
  const { files, missing } = await filesForBackup(onProgress)
  return {
    entries: [{ name: 'ledger.sqlite', bytes: ledger }, ...files.map((f) => ({ name: `attachments/${f.id}`, bytes: f.bytes }))],
    missing,
  }
}

/** Downloads a plain backup (a zip). Returns the ids of files that could not go in. */
export async function downloadBackup(onProgress) {
  const { entries, missing } = await backupEntries(onProgress)
  saveFile(await zip(entries), `${backupStem()}.zip`, 'application/zip')
  return missing
}

/**
 * What a restored backup holds: `{ ledger, files: [{ id, bytes }] }`, from a
 * zip's entries — `ledger.sqlite` (or the one `.sqlite` an older protected
 * backup held) and `attachments/<id>`.
 */
export function readBackup(entries) {
  const ledger = entries.find((e) => e.name === 'ledger.sqlite') ?? entries.find((e) => e.name.endsWith('.sqlite'))
  if (!ledger) throw Object.assign(new Error('There is no financial data in that backup'), { code: 'zip.no-ledger' })
  const files = entries.filter((e) => e.name.startsWith('attachments/')).map((e) => ({ id: e.name.slice('attachments/'.length), bytes: e.bytes }))
  return { ledger: ledger.bytes, files }
}
